/**
 * Bulk Import — history page.
 * Lists all import jobs (all admins), auto-refreshes every 10s.
 */
import { loadFailure } from '../save-failure-words';
import { formatDateTime } from '../../../utils/format-date';
import { useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../../shell/common-header';
import { BulkImportJobDetail } from './bulk-import-job-detail';
import { ImportStatusBadge } from './import-status-badge';
import { ImportJobSummary } from '../../../api/admin/import/types';
import { useImportJobs } from '../../../api/admin/import/hooks';
import { FormMessage } from '../../../components/form-field';
import bulk from './bulk-import.module.css';

interface Props {
  onBack?: () => void;
  focusJobId?: string | null;
  onFocusClear?: () => void;
  /** When true, renders only the inner content — no portal-container/CommonHeader wrapper */
  embedded?: boolean;
}

export function BulkImportHistory({ onBack, focusJobId, onFocusClear, embedded = false }: Props) {
  const { t } = useTranslation();
  const query = useImportJobs(1, 20);
  const jobs: ImportJobSummary[] = query.data?.items ?? [];
  const refreshing = query.isFetching && !query.isLoading;
  const error = query.isError ? loadFailure(t, query.error, 'importHistory') : null;
  const [selectedJobId, setSelectedJobId] = useState<string | null>(focusJobId ?? null);

  function handleBackFromDetail() {
    setSelectedJobId(null);
    onFocusClear?.();
  }

  const jobDetailContent = selectedJobId ? (
    <BulkImportJobDetail jobId={selectedJobId} onBack={handleBackFromDetail} />
  ) : null;

  const historyListContent = (
    <>
      <div className={bulk.bulkImportHistoryHeader}>
        <span />
        {refreshing && (
          <span className={bulk.bulkImportRefreshing}>🔄 {t('admin.bulkImportHistory.refreshing')}</span>
        )}
      </div>

      {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

      {jobs.length === 0 && !error && (
        <p className="tone-soft">{t('admin.bulkImportHistory.empty')}</p>
      )}

      {jobs.map((job) => (
        <div key={job.id} className={bulk.bulkImportJobRow}>
          <div className={bulk.bulkImportJobStatus}>
            <ImportStatusBadge status={job.status} />
          </div>
          <div className={bulk.bulkImportJobMeta}>
            <div>
              {job.appliedOps}/{job.totalOps} ops
              {/* The count comes from the translation's own {{count}}; printing
                  it here as well rendered "2 2 failed". Invisible until a job
                  actually had failures, which is what seeded data is for. */}
              {job.failedOps > 0 && (
                <span className="tone-danger">
                  {' · '}
                  {t('admin.bulkImportHistory.failedCount', { count: job.failedOps })}
                </span>
              )}
            </div>
            <div>{t('admin.bulkImportHistory.byAdmin')}: {job.adminName} · {formatDateTime(job.startedAt)}</div>
          </div>
          <Button type="button" variant="gold" size="sm" onClick={() => setSelectedJobId(job.id)}
          >
            {t('admin.bulkImportHistory.viewButton')}
          </Button>
        </div>
      ))}
    </>
  );

  if (embedded) {
    // No back control here: BulkImportJobDetail ends with its own <Button>,
    // wired to the same handler and the same i18n key. Rendering both put two
    // "Back to list" controls on one screen — a bare text link above the
    // content and a gold Button below it.
    return selectedJobId ? jobDetailContent : historyListContent;
  }

  if (selectedJobId) {
    return (
      <div className="portal-shell portal-container">
        <CommonHeader
          title={t('admin.bulkImportHistory.title')}
          showBackButton
          onBackClick={handleBackFromDetail}
          backLabel={t('common.backToList')}
        />
        <main className="portal-main">
          <div className={`portal-content ${bulk.bulkImportContainer}`}>
            {jobDetailContent}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="portal-shell portal-container">
      <CommonHeader title={t('admin.bulkImportHistory.title')} showBackButton onBackClick={onBack} />
      <main className="portal-main">
        <div className={`portal-content ${bulk.bulkImportContainer}`}>
          {historyListContent}
        </div>
      </main>
    </div>
  );
}
